/**
 * Tenant Detail Page
 * Shows a single tenant's info + status control, and manages its users
 * (create, role change, deactivate/reactivate, reset password).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  adminClient,
  CreateTenantUserRequest,
  TenantSummary,
  TenantUserRole,
  TenantUserSummary,
} from '../../api/adminClient';
import { Toast } from '../../components/Toast';

const ROLES: TenantUserRole[] = ['ADMIN', 'OFFICE_MANAGER', 'TECHNICIAN', 'CUSTOMER_PORTAL'];

const EMPTY_USER_FORM: CreateTenantUserRequest = { email: '', password: '', role: 'OFFICE_MANAGER' };

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();

  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [users, setUsers] = useState<TenantUserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [userForm, setUserForm] = useState<CreateTenantUserRequest>(EMPTY_USER_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [tenantResult, usersResult] = await Promise.all([
        adminClient.getTenant(id),
        adminClient.listTenantUsers(id),
      ]);
      setTenant(tenantResult);
      setUsers(usersResult);
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to load tenant';
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleTenantStatus = async () => {
    if (!tenant) return;
    const nextStatus = tenant.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    try {
      const updated = await adminClient.updateTenant(tenant.id, { status: nextStatus });
      setTenant(updated);
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to update tenant status';
      setLoadError(message);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setFormError(null);
    setIsSubmitting(true);

    try {
      await adminClient.createTenantUser(id, userForm);
      setUserForm(EMPTY_USER_FORM);
      setShowCreateForm(false);
      await loadData();
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to create user';
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleUserActive = async (user: TenantUserSummary) => {
    if (!id) return;
    setPendingUserId(user.id);
    try {
      await adminClient.updateTenantUser(id, user.id, { isActive: !user.isActive });
      await loadData();
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to update user';
      setLoadError(message);
    } finally {
      setPendingUserId(null);
    }
  };

  const handleRoleChange = async (user: TenantUserSummary, role: TenantUserRole) => {
    if (!id || role === user.role) return;
    setPendingUserId(user.id);
    try {
      await adminClient.updateTenantUser(id, user.id, { role });
      await loadData();
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to update role';
      setLoadError(message);
    } finally {
      setPendingUserId(null);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !resetPasswordUserId) return;
    setResetPasswordError(null);

    try {
      await adminClient.resetTenantUserPassword(id, resetPasswordUserId, resetPasswordValue);
      setResetPasswordUserId(null);
      setResetPasswordValue('');
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to reset password';
      setResetPasswordError(message);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-slate-500">Loading...</div>;
  }

  if (loadError && !tenant) {
    return (
      <div className="p-8">
        <Toast kind="error">{loadError}</Toast>
        <p className="text-slate-700">The tenant could not be loaded.</p>
        <Link to="/admin" className="text-slate-700 underline">
          Back to tenants
        </Link>
      </div>
    );
  }

  if (!tenant) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/admin" className="inline-block py-2 sm:py-0 text-slate-300 text-sm hover:text-white">
            &larr; Back to tenants
          </Link>
          <h1 className="text-xl font-bold mt-1 break-words">{tenant.displayName}</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {loadError && <Toast kind="error">{loadError}</Toast>}

        <section className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Tenant Info</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="min-w-0">
              <dt className="text-slate-500">Name</dt>
              <dd className="text-slate-900 break-words">{tenant.name}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-slate-500">Slug</dt>
              <dd className="text-slate-900 break-words">{tenant.slug}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Status</dt>
              <dd>
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                    tenant.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800'
                      : tenant.status === 'SUSPENDED'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {tenant.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Created</dt>
              <dd className="text-slate-900">{new Date(tenant.createdAt).toLocaleString()}</dd>
            </div>
          </dl>
          <button
            onClick={handleToggleTenantStatus}
            className="mt-4 w-full sm:w-auto min-h-[44px] sm:min-h-0 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-800 py-2.5 sm:py-1.5 px-3 rounded-md"
          >
            {tenant.status === 'SUSPENDED' ? 'Reactivate Tenant' : 'Suspend Tenant'}
          </button>
        </section>

        <section className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Users</h2>
            <button
              onClick={() => setShowCreateForm((v) => !v)}
              className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold py-2.5 sm:py-2 px-4 rounded-md"
            >
              {showCreateForm ? 'Cancel' : 'Create User'}
            </button>
          </div>

          {showCreateForm && (
            <form onSubmit={handleCreateUser} className="space-y-4 w-full max-w-md mb-6 border border-slate-200 rounded-md p-3 sm:p-4">
              {formError && <Toast kind="error">{formError}</Toast>}
              <div>
                <label htmlFor="user-email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  id="user-email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label htmlFor="user-password" className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <input
                  id="user-password"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label htmlFor="user-role" className="block text-sm font-medium text-slate-700 mb-1">
                  Role
                </label>
                <select
                  id="user-role"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value as TenantUserRole })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white text-sm font-semibold py-2.5 sm:py-2 px-4 rounded-md"
              >
                {isSubmitting ? 'Creating...' : 'Create User'}
              </button>
            </form>
          )}

          <div className="md:overflow-hidden md:border md:border-slate-200 md:rounded-md">
            <table className="responsive-table min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Active</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td data-label="Email" data-primary className="px-4 py-3 text-slate-900 break-words">{user.email}</td>
                    <td data-label="Role" className="px-4 py-3">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value as TenantUserRole)}
                        disabled={pendingUserId === user.id}
                        className="w-full md:w-auto min-w-0 border border-slate-300 rounded-md text-base md:text-sm py-2 md:py-1 px-2"
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Active" className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                          user.isActive ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-700'
                        }`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Created" className="px-4 py-3 text-slate-600">{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 flex-wrap md:space-x-3">
                      <button
                        onClick={() => handleToggleUserActive(user)}
                        disabled={pendingUserId === user.id}
                        className="py-2.5 md:py-0 text-sm font-medium text-slate-700 hover:text-slate-900 disabled:text-slate-400"
                      >
                        {user.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                      <button
                        onClick={() => {
                          setResetPasswordUserId(user.id);
                          setResetPasswordValue('');
                          setResetPasswordError(null);
                        }}
                        className="py-2.5 md:py-0 text-sm font-medium text-slate-700 hover:text-slate-900"
                      >
                        Reset Password
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {resetPasswordUserId && (
            <form
              onSubmit={handleResetPassword}
              className="mt-4 w-full max-w-md border border-slate-200 rounded-md p-3 sm:p-4 space-y-3"
            >
              <h3 className="text-sm font-semibold text-slate-800 break-words">
                Reset password for{' '}
                {users.find((u) => u.id === resetPasswordUserId)?.email}
              </h3>
              {resetPasswordError && <Toast kind="error">{resetPasswordError}</Toast>}
              <input
                type="password"
                value={resetPasswordValue}
                onChange={(e) => setResetPasswordValue(e.target.value)}
                placeholder="New password"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold py-2.5 sm:py-1.5 px-3 rounded-md"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setResetPasswordUserId(null)}
                  className="py-2.5 sm:py-0 text-sm text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
